import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
// Phosphor for site-wide icon language. Use the regular weight for nav and
// controls (matches Lucide's stroke aesthetic) and reserve duotone for
// display/hero contexts (model cards, etc.).
import {
    FilmSlate as Clapperboard,
    GearSix as Settings,
    CaretLeft as ChevronLeft,
    CaretRight as ChevronRight,
    Power,
    Sparkle as Sparkles,
    FolderOpen,
    Folder,
    HardDrives as HardDrive,
    Cube as Box,
    Clock,
} from '@phosphor-icons/react';
// Banana lives in Lucide, not Phosphor. Imported here so the Media Lab nav
// item gets a banana icon (the brand cue for the image-gen tier headline).
import { Banana } from 'lucide-react';
import LogoHead from '../components/logos/LogoHead';
import { useAuth } from '../context/AuthContext';
import { useApp }  from '../context/AppContext';
import CreditAlertModal from '../components/Modals/CreditAlertModal';
import MobileGate from '../components/UI/MobileGate';
import ObjectUndoBanner from '../components/UI/ObjectUndoBanner';
import ToastContainer from '../components/UI/ToastContainer';
import { getStorageLimitBytes, getStorageLimitLabel, formatBytes } from '../constants/tiers';

const MainLayout = () => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { user, logout }   = useAuth();
    const { projectMedia, getProjectsList, loadProject, projectId } = useApp();
    const navigate           = useNavigate();

    // ── Recent projects (top 5) — read from localStorage on each render so
    // the list stays fresh as the user creates/saves projects.
    const recentProjects = (() => {
        try {
            return (getProjectsList?.() || [])
                .slice()
                .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
                .slice(0, 5);
        } catch { return []; }
    })();

    // Refs so the capture-phase listener never goes stale
    const asideRef       = useRef(null);
    const isCollapsedRef = useRef(false);
    useEffect(() => { isCollapsedRef.current = isCollapsed; }, [isCollapsed]);

    // Capture-phase mousedown — fires before ReactFlow (or anything else) can
    // call stopPropagation, so it works on the canvas AND the timeline.
    useEffect(() => {
        const handleDown = (e) => {
            if (isCollapsedRef.current) return;               // already collapsed
            if (asideRef.current?.contains(e.target)) return; // click was inside the nav
            setIsCollapsed(true);
        };
        document.addEventListener('mousedown', handleDown, true); // capture = true
        return () => document.removeEventListener('mousedown', handleDown, true);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getTierDisplay = (tier) => {
        const tiers = {
            free:       'Free Plan',
            creator:    'Creator',
            pro:        'Pro',
            enterprise: 'Enterprise',
        };
        return tiers[tier] || 'Free Plan';
    };

    // ── Storage computation ───────────────────────────────────────────────────
    // Sum up blobData.size for locally-uploaded files (client-side approximation).
    // When the backend exposes user.storageUsed, prefer that instead.
    const storageUsedBytes = user?.storageUsed != null
        ? user.storageUsed
        : projectMedia.reduce((sum, m) => sum + (m.blobData?.size ?? 0), 0);

    const storageLimitBytes = getStorageLimitBytes(user?.tier);
    const storageLimitLabel = getStorageLimitLabel(user?.tier);
    const storagePercent    = storageLimitBytes > 0
        ? (storageUsedBytes / storageLimitBytes) * 100
        : 0;

    const storageColor =
        storagePercent > 85 ? '#ef4444' :
        storagePercent > 60 ? '#f59e0b' :
        'var(--primary-color)';

    return (
        <MobileGate>
        <div className="flex h-screen w-full overflow-hidden text-white bg-[url('/bg-noise.png')]">
            <CreditAlertModal />
            <ObjectUndoBanner />
            <ToastContainer />

            {/* ── Main Navigation Sidebar ── */}
            <aside
                ref={asideRef}
                className={`glass-panel border-r-0 z-20 flex items-stretch transition-all duration-300 ${
                    isCollapsed ? 'w-20' : 'w-64'
                }`}
            >
                {/* Nav content */}
                <div className="flex-1 flex flex-col justify-between py-6 overflow-hidden min-w-0">
                    <div>
                        {/* Logo */}
                        <div className="flex items-center px-4 mb-10 justify-center">
                            <div className="w-10 h-10 flex items-center justify-center shrink-0">
                                <LogoHead className="w-full h-full" />
                            </div>
                            {!isCollapsed && (
                                <span className="ml-3 font-heading font-bold text-2xl tracking-tight animate-fade-in">Helix</span>
                            )}
                        </div>

                        {/* Nav Links */}
                        <nav className="flex flex-col gap-2 px-2">
                            <NavItem to="/projects"      icon={<Folder       size={22} />} label="Projects"         isCollapsed={isCollapsed} />
                            <NavItem to="/storyboard"    icon={<Clapperboard size={22} />} label="Storyboard"       isCollapsed={isCollapsed} />
                            <NavItem to="/media-lab"     icon={<Banana       size={22} />} label="Media Lab"        isCollapsed={isCollapsed} />
                            <NavItem to="/objects"       icon={<Box          size={22} />} label="Objects"          isCollapsed={isCollapsed} />
                            <NavItem to="/project-media" icon={<FolderOpen   size={22} />} label="Project Media"    isCollapsed={isCollapsed} />
                        </nav>

                        {/* Recent Projects — only shown expanded; up to 5 most-recent */}
                        {!isCollapsed && recentProjects.length > 0 && (
                            <div className="mt-6 px-2">
                                <div className="px-3 mb-2 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-white/30">
                                    <Clock size={9} />
                                    Recent Projects
                                </div>
                                <ul className="flex flex-col gap-0.5">
                                    {recentProjects.map(p => {
                                        const isActive = p.id === projectId;
                                        return (
                                            <li key={p.id}>
                                                <button
                                                    onClick={async () => {
                                                        try { await loadProject(p.id); navigate('/storyboard'); }
                                                        catch (e) { console.warn('Project load failed', e); }
                                                    }}
                                                    className={`w-full text-left px-3 py-1.5 rounded-lg text-xs truncate transition-colors ${
                                                        isActive
                                                            ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-400/25'
                                                            : 'text-white/55 hover:text-white hover:bg-white/[0.04] border border-transparent'
                                                    }`}
                                                    title={p.name}
                                                >
                                                    {p.name || 'Untitled project'}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* ── Bottom section ── */}
                    <div className="px-2 flex flex-col gap-2">

                        {/* Credit Balance */}
                        {!isCollapsed && user && (
                            <div className="p-3 rounded-xl glass-panel flex items-center gap-2">
                                <span className="text-xl leading-none select-none" title="Credits">🍌</span>
                                <div className="flex-1">
                                    <p className="text-xs text-[var(--text-secondary)]">Credits</p>
                                    <p className="text-sm font-medium text-yellow-400">{user.creditBalance?.toLocaleString() || 0}</p>
                                </div>
                            </div>
                        )}
                        {isCollapsed && user && (
                            <div
                                className="flex justify-center p-3 cursor-default"
                                title={`Credits: ${user.creditBalance?.toLocaleString() || 0}`}
                            >
                                <span className="text-xl leading-none select-none">🍌</span>
                            </div>
                        )}

                        {/* ── Storage tracker ── */}
                        {!isCollapsed && user && (
                            <div className="p-3 rounded-xl glass-panel space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <HardDrive size={13} style={{ color: storageColor }} />
                                        <span className="text-xs text-[var(--text-secondary)]">Storage</span>
                                    </div>
                                    <span className="text-[10px] text-white/40 tabular-nums font-mono">
                                        {formatBytes(storageUsedBytes)}
                                    </span>
                                </div>
                                {/* Progress bar */}
                                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width:           `${Math.min(100, storagePercent)}%`,
                                            backgroundColor: storageColor,
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[9px] text-white/25">{storageLimitLabel} limit</span>
                                    <span
                                        className="text-[9px] font-bold tabular-nums"
                                        style={{ color: storagePercent > 60 ? storageColor : 'rgba(255,255,255,0.25)' }}
                                    >
                                        {Math.round(storagePercent)}%
                                    </span>
                                </div>
                            </div>
                        )}
                        {isCollapsed && user && (
                            <div
                                className="flex justify-center p-3 cursor-default"
                                title={`Storage: ${formatBytes(storageUsedBytes)} / ${storageLimitLabel}`}
                            >
                                <HardDrive size={20} style={{ color: storagePercent > 60 ? storageColor : 'rgba(255,255,255,0.25)' }} />
                            </div>
                        )}

                        {/* User Profile — click to open /account */}
                        <div
                            onClick={() => navigate('/account')}
                            title={isCollapsed ? 'Account & Billing' : ''}
                            className={`p-3 rounded-xl glass-panel cursor-pointer hover:bg-[rgba(255,255,255,0.1)] transition-colors group flex items-center ${isCollapsed ? 'justify-center' : 'justify-start gap-3'}`}
                        >
                            <div className="w-8 h-8 rounded-full bg-gray-700 overflow-hidden border border-gray-500 group-hover:border-[var(--primary-color)] transition-colors flex-shrink-0">
                                <img
                                    src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email || 'Helix'}`}
                                    alt="User"
                                />
                            </div>
                            {!isCollapsed && (
                                <div className="overflow-hidden whitespace-nowrap flex-1">
                                    <p className="text-sm font-medium">{user?.displayName || user?.name || (user?.email ? user.email.split('@')[0] : 'User')}</p>
                                    <p className="text-xs text-[var(--text-secondary)]">{getTierDisplay(user?.tier)}</p>
                                </div>
                            )}
                        </div>

                        <NavItem to="/settings" icon={<Settings size={22} />} label="Settings" isCollapsed={isCollapsed} />

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className={`flex items-center p-3 rounded-xl transition-all duration-200 group relative overflow-hidden text-[var(--text-secondary)] hover:bg-[rgba(255,100,100,0.1)] hover:text-red-400 ${isCollapsed ? 'justify-center' : 'justify-start gap-4'}`}
                            title={isCollapsed ? 'Logout' : ''}
                        >
                            <span className="relative z-10 flex-shrink-0"><Power size={22} /></span>
                            {!isCollapsed && (
                                <span className="font-medium relative z-10 whitespace-nowrap overflow-hidden text-ellipsis">Logout</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Collapse tab — right edge, always visible, matches drawer style ── */}
                <button
                    onClick={() => setIsCollapsed(v => !v)}
                    title={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                    className="w-5 shrink-0 flex items-center justify-center bg-[rgba(8,8,18,0.6)] border-l border-[var(--border-glass)] hover:bg-[rgba(24,181,240,0.08)] hover:border-[var(--primary-color)]/30 transition-all group"
                >
                    {isCollapsed
                        ? <ChevronRight size={12} className="text-white/30 group-hover:text-[var(--primary-color)] transition-colors" />
                        : <ChevronLeft  size={12} className="text-white/30 group-hover:text-[var(--primary-color)] transition-colors" />
                    }
                </button>
            </aside>

            {/* ── Main Content Area ── */}
            <main className="flex-1 relative overflow-hidden flex flex-col min-w-0">
                {/* Soft storage warning at 80% — persists until user upgrades or clears space */}
                {storagePercent >= 80 && storagePercent < 100 && (
                    <div className="px-4 pt-3 z-20">
                        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-400/30 backdrop-blur">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-300 shrink-0">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                            <p className="text-xs text-amber-100 flex-1">
                                <span className="font-bold">Storage at {Math.round(storagePercent)}%</span>
                                <span className="text-amber-200/70"> · {formatBytes(storageUsedBytes)} of {storageLimitLabel}. Free up space or upgrade before you hit the cap.</span>
                            </p>
                            <Link
                                to="/account?tab=subscription"
                                className="shrink-0 px-3 py-1 rounded-md bg-amber-400/90 hover:bg-amber-300 text-black text-[10px] font-bold uppercase tracking-wider transition-colors"
                            >
                                Upgrade
                            </Link>
                        </div>
                    </div>
                )}
                {storagePercent >= 100 && (
                    <div className="px-4 pt-3 z-20">
                        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-400/30 backdrop-blur">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-300 shrink-0">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <p className="text-xs text-red-100 flex-1">
                                <span className="font-bold">Storage limit reached.</span>
                                <span className="text-red-200/70"> Uploads are blocked until you free space or upgrade your plan.</span>
                            </p>
                            <Link
                                to="/account?tab=subscription"
                                className="shrink-0 px-3 py-1 rounded-md bg-red-400/90 hover:bg-red-300 text-black text-[10px] font-bold uppercase tracking-wider transition-colors"
                            >
                                Upgrade
                            </Link>
                        </div>
                    </div>
                )}
                <div className="flex-1 overflow-auto p-4 relative z-10">
                    <Outlet />
                </div>
            </main>

        </div>
        </MobileGate>
    );
};

const NavItem = ({ to, icon, label, isCollapsed }) => (
    <NavLink
        to={to}
        className={({ isActive }) =>
            `flex items-center p-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                isCollapsed ? 'justify-center' : 'justify-start gap-4'
            } ${isActive
                ? 'bg-[rgba(24,181,240,0.15)] text-[var(--primary-color)] shadow-[0_0_10px_rgba(24,181,240,0.2)] border border-[rgba(24,181,240,0.3)]'
                : 'text-[var(--text-secondary)] hover:bg-[rgba(255,255,255,0.05)] hover:text-white'
            }`
        }
        title={isCollapsed ? label : ''}
    >
        <span className="relative z-10 flex-shrink-0">{icon}</span>
        {!isCollapsed && (
            <span className="font-medium relative z-10 whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
        )}
        {/* Hover glow */}
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(24,181,240,0.1)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </NavLink>
);

export default MainLayout;
